import { useState, useEffect, useCallback, useRef } from 'react';
import logger from '../shared/utils/logger';
import { getErrorMessage } from '../shared/utils/errorMessage';
import { useAuth } from '../contexts/AuthContext';
import notificationService, { 
  Notification, 
  NotificationCount, 
  NotificationFilters 
} from '../services/notification-service';

/**
 * Custom hook for managing notifications
 * 
 * Provides functions for fetching, marking as read, and managing notifications
 */
export const useNotifications = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationCount, setNotificationCount] = useState<NotificationCount>({ total: 0, unread: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<NotificationFilters>({
    page: 1,
    limit: 20,
    sortField: 'created_at',
    sortDirection: 'DESC'
  });
  const [totalPages, setTotalPages] = useState(1);
  const hasInitialized = useRef<boolean>(false);
  
  // Fetch notifications with pagination
  const fetchNotificationsPaginated = useCallback(async (pageFilters?: NotificationFilters) => {
    if (!user?.id) {
      logger.warn('Cannot fetch notifications: No user ID available');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      const currentFilters = { ...filters, ...pageFilters };
      const response = await notificationService.getNotificationsPaginated(currentFilters);
      
      if (response && response.data) {
        setNotifications(response.data);
        setNotificationCount({
          total: response.meta.total,
          unread: response.unread_count
        });
        setTotalPages(response.meta.pages);
        setFilters(currentFilters);
      } else {
        logger.warn('Received invalid notifications response:', response);
        setNotifications([]);
      }
    } catch (err: unknown) {
      logger.error('Error fetching paginated notifications:', err);
      setError(getErrorMessage(err, 'Failed to fetch notifications'));
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, filters]);

  // Subscribe to notifications when user is authenticated
  useEffect(() => {
    if (user?.id) {
      // Subscribe to new notifications
      const unsubscribe = notificationService.subscribe(notification => {
        // Update notification count
        setNotificationCount(prev => ({
          ...prev,
          unread: prev.unread + 1
        }));
        
        // We don't auto-refresh here to avoid recursive API calls
      });
      
      // Clean up on unmount
      return () => {
        unsubscribe();
      };
    }
  }, [user?.id]);

  // Initialize - fetch notifications only once when user changes
  useEffect(() => {
    if (user?.id && !hasInitialized.current) {
      // Fetch initial notifications once
      fetchNotificationsPaginated();
      hasInitialized.current = true;
    }
    
    // Reset when user changes
    if (!user?.id) {
      hasInitialized.current = false;
    }
  }, [user?.id, fetchNotificationsPaginated]);

  // Fetch all notifications (for backward compatibility)
  const fetchNotifications = useCallback(async () => {
    if (!user?.id) {
      logger.warn('Cannot fetch notifications: No user ID available');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      const data = await notificationService.getNotifications();
      
      // Ensure we always have an array
      if (Array.isArray(data)) {
        setNotifications(data);
      } else {
        logger.warn('Received non-array notifications data:', data);
        setNotifications([]);
      }
      
      // Update notification count
      await fetchNotificationCount();
    } catch (err: unknown) {
      logger.error('Error fetching notifications:', err);
      setError(getErrorMessage(err, 'Failed to fetch notifications'));
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  // Fetch notification count
  const fetchNotificationCount = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const count = await notificationService.getNotificationCount();
      setNotificationCount(count);
    } catch (err: unknown) {
      logger.error('Error fetching notification count:', err);
      // Calculate count from notifications we already have
      const unreadCount = notifications.filter(n => !n.isRead).length;
      setNotificationCount({
        total: notifications.length,
        unread: unreadCount
      });
    }
  }, [user?.id, notifications]);

  // Mark a notification as read
  const markAsRead = useCallback(async (id: number) => {
    try {
      await notificationService.markAsRead(id);
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => 
          notification.notificationId === id ? { ...notification, isRead: true } : notification
        )
      );
      
      // Update notification count
      setNotificationCount(prev => ({
        ...prev,
        unread: Math.max(0, prev.unread - 1)
      }));
    } catch (err: unknown) {
      logger.error('Error marking notification as read:', err);
      // Refresh notifications to ensure UI is in sync with server
      fetchNotifications();
    }
  }, [fetchNotifications]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead();
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, isRead: true }))
      );
      
      // Update notification count
      setNotificationCount(prev => ({
        ...prev,
        unread: 0
      }));
    } catch (err: unknown) {
      logger.error('Error marking all notifications as read:', err);
      // Refresh notifications to ensure UI is in sync with server
      fetchNotifications();
    }
  }, [fetchNotifications]);

  // Change page
  const changePage = useCallback((page: number) => {
    fetchNotificationsPaginated({ ...filters, page });
  }, [filters, fetchNotificationsPaginated]);

  // Change filters
  const changeFilters = useCallback((newFilters: NotificationFilters) => {
    fetchNotificationsPaginated({ ...filters, ...newFilters, page: 1 });
  }, [filters, fetchNotificationsPaginated]);

  return {
    notifications,
    notificationCount,
    isLoading,
    error,
    filters,
    totalPages,
    fetchNotifications,
    fetchNotificationsPaginated,
    fetchNotificationCount,
    markAsRead,
    markAllAsRead,
    changePage,
    changeFilters
  };
}; 